import { useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button as UiButton } from "@shadcn-in-nx/ui/button";
import { track } from "@crm/analytics";
import { Bluelagoon } from "@bluelagoon";

export function AddCardForm({ onSuccess, onCancel, userId, addPaymentMethod, t, clientSecret }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [setAsDefault, setSetAsDefault] = useState(true);
    const [paymentElementComplete, setPaymentElementComplete] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            // Submit the PaymentElement to validate
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setError(submitError.message);
                setIsProcessing(false);
                return;
            }

            // Confirm the SetupIntent with PaymentElement
            const { error: confirmError, setupIntent } = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: "if_required",
            });

            if (confirmError) {
                setError(confirmError.message);
                setIsProcessing(false);
                return;
            }

            if (setupIntent.status === "succeeded") {
                // Set as default if checked
                if (setAsDefault) {
                    await Bluelagoon.setDefaultPaymentMethod({
                        userId,
                        paymentMethodId: setupIntent.payment_method,
                    });
                }

                track("payment_method_added");
                onSuccess();
            } else {
                setError(`Setup failed with status: ${setupIntent.status}`);
            }
        } catch (err) {
            console.error("Error adding payment method:", err);
            const errorMessage = err?.error || err?.message || "An unexpected error occurred";
            setError(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("billing.paymentDetails") || "Payment Details"}
                </label>
                <PaymentElement
                    options={{
                        layout: "tabs",
                        paymentMethodOrder: ["card", "crypto", "apple_pay", "google_pay", "link"],
                    }}
                    onChange={(e) => setPaymentElementComplete(e.complete)}
                />
            </div>

            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="setAsDefault"
                    checked={setAsDefault}
                    onChange={(e) => setSetAsDefault(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="setAsDefault" className="ml-2 text-sm text-gray-700">
                    {t("billing.setAsDefaultPayment") || "Set as default payment method"}
                </label>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            <div className="flex gap-3 justify-end">
                <UiButton
                    type="button"
                    onClick={onCancel}
                    variant="outline"
                    disabled={isProcessing}
                >
                    {t("common.cancel") || "Cancel"}
                </UiButton>
                <UiButton
                    type="submit"
                    disabled={!stripe || isProcessing || !paymentElementComplete}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                    {isProcessing ? (t("billing.processing") || "Processing...") : (t("billing.addPaymentMethod") || "Add Payment Method")}
                </UiButton>
            </div>
        </form>
    );
}
